import React, { useEffect, useState, useMemo, useCallback } from "react"
import { Offcanvas, Row, Col, Form, InputGroup, Button, Badge, Spinner } from "react-bootstrap"
import { useHistory } from "react-router-dom"
import { useTranslation } from "react-i18next"
import API from "@/common/API"
import { parsedUser } from "@/common/GetCurrentUser"
import { Routes } from "@/routes"
import Settings from "@/common/Settings"
import moment from "moment"
import Pagination from "@/components/core/Pagination"
import { confirmAlert } from "react-confirm-alert"

interface VisitHistorySidebarProps {
    show: boolean
    toggle: () => void
    placement?: "start" | "end"
    allowMapInteraction?: boolean
}

interface HistoryRecord {
    id?: string
    placeId: string
    placeName: string
    note: string
    image: string
    type?: "visit" | "place_creation" | string
    author?: {
        firstName?: string
        lastName?: string
        email?: string
    }
    created: string
}

const VisitHistorySidebar: React.FC<VisitHistorySidebarProps> = ({
    show,
    toggle,
    placement = "end",
    allowMapInteraction = true
}) => {
    const { t } = useTranslation()
    const history = useHistory()
    const user = parsedUser()

    // Estados principales
    const [records, setRecords] = useState<HistoryRecord[]>([])
    const [loading, setLoading] = useState<boolean>(false)
    const [error, setError] = useState<string>("")
    const [isPinned, setIsPinned] = useState<boolean>(true) // Pinned por defecto para interacción con mapa
    const [filterType, setFilterType] = useState<"all" | "visit" | "place_creation">("all")

    // Estados de paginación
    const [page, setPage] = useState<number>(0)
    const [quantityOfPages, setQuantityOfPages] = useState<number>(1)
    const [totalRecords, setTotalRecords] = useState<number>(0)
    const quantity = 15 // Reducido para carga más rápida

    // Estados de filtros
    const [searchTerm, setSearchTerm] = useState<string>("")
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("")

    // Debounce para el término de búsqueda
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm)
        }, 500)

        return () => clearTimeout(timer)
    }, [searchTerm])

    // Función memoizada para obtener historial
    const requestHistories = useCallback(async (resetPage = false) => {
        if (!user?.OrganizationId) return

        setLoading(true)
        setError("")

        try {
            const currentPage = resetPage ? 0 : page
            let queryParams: Record<string, string> = {
                page: currentPage.toString(),
                quantity: quantity.toString(),
                organizationId: user?.OrganizationId?.toString() || '',
                OrganizationId: user?.OrganizationId?.toString() || '',
            }
            if (debouncedSearchTerm) {
                queryParams.name = debouncedSearchTerm
            }
            if (filterType && filterType !== "all") {
                queryParams.type = filterType
            }

            let query = new URLSearchParams(queryParams).toString()
            let response: any
            try {
                response = await API.getAction("place/history", query)
            } catch (firstErr: any) {
                // If combined query fails on remote server (e.g. 400 Bad Request), fallback to type=visit
                if (filterType === "all") {
                    delete queryParams.type
                    queryParams.type = "visit"
                    query = new URLSearchParams(queryParams).toString()
                    response = await API.getAction("place/history", query)
                } else {
                    throw firstErr
                }
            }

            if (response?.data && response.data.status === "ok") {
                setRecords(response.data.response || [])
                setQuantityOfPages(response.data.quantityOfPages || 1)
                setTotalRecords(response.data.quantityOfRecords || 0)

                if (resetPage) {
                    setPage(0)
                }
            } else {
                setError(t("Error loading history"))
            }
        } catch (err) {
            console.error("Error fetching history:", err)
            setError(t("Error loading history"))
        } finally {
            setLoading(false)
        }
    }, [page, quantity, user?.OrganizationId, debouncedSearchTerm, filterType, t])

    // Efecto para cargar datos cuando cambia el término de búsqueda o tipo de filtro
    useEffect(() => {
        if (show) {
            requestHistories(true) // Reset page cuando hay nueva búsqueda o filtro
        }
    }, [debouncedSearchTerm, filterType, show])

    // Efecto para cargar datos cuando cambia la página
    useEffect(() => {
        if (show && page > 0) {
            requestHistories(false)
        }
    }, [page, show])

    // Efecto inicial para cargar datos
    useEffect(() => {
        if (show) {
            requestHistories(true)
        }
    }, [show])

    // Función para navegar a lugar específico
    const handlePlaceClick = useCallback((placeId: string) => {
        history.push(Routes.PlaceDetails.clean + placeId)
        // Solo cerrar si no está pinned o si el usuario lo desea
        if (!isPinned) {
            toggle()
        }
    }, [history, toggle, isPinned])

    // Función para toggle pin state
    const handleTogglePin = useCallback(() => {
        setIsPinned(!isPinned)
    }, [isPinned])

    // Función para limpiar búsqueda
    const handleClearSearch = useCallback(() => {
        setSearchTerm("")
    }, [])

    const deleteRecord = async (item: any) => {
        if (item) {
            confirmAlert({
                closeOnClickOutside: false,
                message: t("Are you sure you want to delete this record?") ?? "",
                buttons: [
                    {
                        label: t("Yes"),
                        onClick: async () => {
                            await API.getAction("placeVisit/Delete" + "?id=" + item.id)
                            await requestHistories(false);

                        },
                    },
                    {
                        label: "No",
                        onClick: () => {
                            console.log("no")
                        },
                    },
                ],
            })
        }
    }

    // Recordes memoizados para evitar re-renders innecesarios
    const recordsList = useMemo(() => {
        if (!records || records.length === 0) {
            return (
                <div className="text-center py-5">
                    <div className="text-muted">
                        <i className="fa fa-history fa-3x mb-3 opacity-50"></i>
                        <h6 className="mb-2">{t("No history available")}</h6>
                        <small>{debouncedSearchTerm ? t("No results found for your search") : t("No history records found")}</small>
                    </div>
                </div>
            )
        }

        return records.map((item: HistoryRecord, index: number) => {
            const isCreation = item.type === "place_creation"
            const authorName = item.author ? `${item.author.firstName ?? ""} ${item.author.lastName ?? ""}`.trim() : ""

            return (
                <div
                    key={`${item.placeId}-${item.id || index}`}
                    className="border-bottom pb-3 mb-3 cursor-pointer"
                    style={{
                        transition: "background-color 0.2s ease",
                        cursor: "pointer",
                        borderRadius: "8px",
                        padding: "8px"
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#f8f9fa"
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent"
                    }}
                >
                    <Row className="g-2 align-items-center">
                        <Col xs={4}>
                            <div className="position-relative" onClick={() => handlePlaceClick(item.placeId)}>
                                {item.image ? (
                                    <img
                                        src={`${Settings.BasePathForAPI}file/getThumbnail?filename=${item.image}`}
                                        loading="lazy"
                                        alt={item.placeName}
                                        className="img-fluid rounded"
                                        style={{
                                            height: "60px",
                                            width: "100%",
                                            objectFit: "cover"
                                        }}
                                        onError={(e) => {
                                            e.currentTarget.src = "/placeholder-image.png"
                                        }}
                                    />
                                ) : (
                                    <div
                                        className="rounded bg-primary bg-opacity-10 d-flex align-items-center justify-content-center text-primary fw-bold border"
                                        style={{ height: "60px", width: "100%", fontSize: "1.1rem" }}
                                    >
                                        {item.placeName ? item.placeName.charAt(0).toUpperCase() : <i className="fa fa-building" />}
                                    </div>
                                )}
                            </div>
                        </Col>
                        <Col xs={8}>
                            <div>
                                <div className="d-flex align-items-center justify-content-between mb-1">
                                    {isCreation ? (
                                        <Badge bg="primary" className="fw-normal" style={{ fontSize: "0.68rem" }}>
                                            <i className="fa fa-plus-circle me-1" />
                                            {t("Lugar Creado")}
                                        </Badge>
                                    ) : (
                                        <Badge bg="success" className="fw-normal" style={{ fontSize: "0.68rem" }}>
                                            <i className="fa fa-walking me-1" />
                                            {t("Visita")}
                                        </Badge>
                                    )}
                                    <span className="text-muted" style={{ fontSize: "0.72rem" }} title={moment(item.created).format("DD/MM/YYYY HH:mm")}>
                                        {moment(item.created).fromNow()}
                                    </span>
                                </div>
                                <h6 className="mb-1 text-truncate fw-bold text-primary"
                                    style={{ fontSize: "0.88rem", cursor: "pointer" }}
                                    onClick={() => handlePlaceClick(item.placeId)}
                                    title={item.placeName}
                                >
                                    {item.placeName}
                                </h6>
                                {item.note && (
                                    <p className="mb-1 text-muted small text-truncate" style={{ fontSize: "0.78rem" }} title={item.note}>
                                        {item.note}
                                    </p>
                                )}
                                <div className="d-flex justify-content-between align-items-center mt-1">
                                    <small className="text-secondary text-truncate" style={{ fontSize: "0.74rem" }} title={authorName}>
                                        <i className="fa fa-user me-1 text-muted" />
                                        {authorName || "Sistema"}
                                    </small>
                                    {!isCreation && (
                                        <button
                                            className="btn btn-sm btn-outline-danger border-0 p-1 py-0"
                                            title={t("Eliminar visita")}
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                deleteRecord(item)
                                            }}
                                        >
                                            <i className="fa fa-trash small"></i>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </Col>
                    </Row>
                </div>
            )
        })
    }, [records, debouncedSearchTerm, handlePlaceClick, t])

    return (
        <Offcanvas
            show={show}
            onHide={toggle}
            placement={placement}
            backdrop={false} // Permitir interacción con el mapa
            scroll={true}    // Permitir scroll en el fondo
            style={{ width: "min(390px, 100vw)", zIndex: 1040 }}
            className={`visit-history-sidebar `}
        >
            <Offcanvas.Header closeButton className="border-bottom">
                <Offcanvas.Title className="d-flex align-items-center">
                    <i className="fa fa-history me-2 text-primary"></i>
                    {t("Historial de Actividad")}
                    {totalRecords > 0 && (
                        <Badge bg="primary" pill className="ms-2 small">
                            {totalRecords}
                        </Badge>
                    )}
                </Offcanvas.Title>

                {/* Pin/Unpin button para mantener abierto mientras se interactúa con mapa */}
                <div className="d-flex align-items-center">
                    <Button
                        variant="outline-light"
                        size="sm"
                        onClick={handleTogglePin}
                        className="me-2"
                        title={isPinned ? t("Unpin - Close when clicking places") : t("Pin - Keep open when clicking places")}
                    >
                        <i className={`fa ${isPinned ? 'fa-thumbtack' : 'fa-map-pin'}`}></i>
                    </Button>
                </div>
            </Offcanvas.Header>

            <Offcanvas.Body className="p-0">
                {/* Search & Filter Section */}
                <div className="p-3 bg-light border-bottom">
                    <InputGroup size="sm">
                        <Form.Control
                            type="text"
                            placeholder={t("Buscar por lugar o agente...")}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            disabled={loading}
                        />
                        {searchTerm && (
                            <Button
                                variant="outline-secondary"
                                onClick={handleClearSearch}
                                disabled={loading}
                            >
                                <i className="fa fa-times"></i>
                            </Button>
                        )}
                        {loading && (
                            <Button variant="outline-secondary" disabled>
                                <Spinner as="span" animation="border" size="sm" />
                            </Button>
                        )}
                    </InputGroup>

                    {/* Filter Type Pills */}
                    <div className="btn-group w-100 mt-2 shadow-2xs" role="group" aria-label="Filtro de Historial">
                        <button
                            type="button"
                            className={`btn btn-sm py-1 fw-semibold ${filterType === "all" ? "btn-primary" : "btn-outline-secondary bg-white"}`}
                            onClick={() => { setFilterType("all"); setPage(0); }}
                        >
                            {t("Todos")}
                        </button>
                        <button
                            type="button"
                            className={`btn btn-sm py-1 fw-semibold ${filterType === "visit" ? "btn-primary" : "btn-outline-secondary bg-white"}`}
                            onClick={() => { setFilterType("visit"); setPage(0); }}
                        >
                            <i className="fa fa-walking me-1" />
                            {t("Visitas")}
                        </button>
                        <button
                            type="button"
                            className={`btn btn-sm py-1 fw-semibold ${filterType === "place_creation" ? "btn-primary" : "btn-outline-secondary bg-white"}`}
                            onClick={() => { setFilterType("place_creation"); setPage(0); }}
                        >
                            <i className="fa fa-plus-circle me-1" />
                            {t("Lugares Creados")}
                        </button>
                    </div>
                </div>

                {/* Content Section */}
                <div className="flex-grow-1 p-3" style={{ height: "calc(100vh - 200px)", overflowY: "auto" }}>
                    {error ? (
                        <div className="alert alert-danger" role="alert">
                            <i className="fa fa-exclamation-triangle me-2"></i>
                            {error}
                            <Button
                                size="sm"
                                variant="outline-danger"
                                className="ms-2"
                                onClick={() => requestHistories(true)}
                            >
                                {t("Retry")}
                            </Button>
                        </div>
                    ) : (
                        recordsList
                    )}
                </div>

                {/* Pagination Section */}
                {quantityOfPages > 1 && !loading && (
                    <div className="border-top p-3 bg-white">
                        <Pagination
                            currentPage={page}
                            totalPages={quantityOfPages}
                            onPageChanged={setPage}
                        />
                    </div>
                )}
            </Offcanvas.Body>
        </Offcanvas>
    )
}

export default VisitHistorySidebar
